import React from "react";
import { getLanguageValue, getCurrentLanguage } from "locales/i18n";
import { PAGE_TYPE } from "enums";

interface PriceItem {
  stadia: number;
  bonus: number;
  totalStadia: number;
  cost: string;
  badgeKey?: "recommended" | "bestSeller";
}

const formatNumber = (num: number) => {
  const locale = getCurrentLanguage() === "ko" ? "ko-KR" : "en-US";
  return num.toLocaleString(locale);
};

const PriceCard = ({ item }: { item: PriceItem }) => {
  const hasBadge = !!item.badgeKey;
  const isRecommended = item.badgeKey === "recommended";
  const badgeText = item.badgeKey
    ? getLanguageValue(PAGE_TYPE.COMMON, `price.badge.${item.badgeKey}`)
    : "";

  return (
    <div
      className={`
        relative bg-white rounded-2xl shadow-sm border overflow-hidden
        flex flex-col min-h-[220px] sm:min-h-[240px]
        transition-shadow hover:shadow-md
        ${hasBadge ? (isRecommended ? "border-pink-200" : "border-purple-200") : "border-gray-200"}
      `}
    >
      {/* 배지 - 좌측 상단 */}
      {hasBadge && (
        <div className="absolute top-3 left-3 sm:top-4 sm:left-4 z-10">
          <span
            className={`
              inline-block px-3 py-1.5 rounded-lg text-xs font-semibold
              ${isRecommended ? "bg-pink-100 text-pink-700" : "bg-purple-100 text-purple-700"}
            `}
          >
            {badgeText}
          </span>
        </div>
      )}

      {/* 스타디아 아이콘 - 가장 상단 */}
      <div className="pt-4 pb-4 sm:pb-5 flex justify-center">
        <img
          src="/imgs/common/stardia.svg"
          alt=""
          className="w-16 h-16 sm:w-20 sm:h-20"
          aria-hidden
        />
      </div>

      {/* Bonus + 숫자 */}
      <div className="px-5 pt-1 pb-0 text-center">
        <p className={`text-sm ${item.bonus > 0 ? "font-bold text-black" : "text-gray-500"}`}>
          {getLanguageValue(PAGE_TYPE.COMMON, "price.txtBonus")}{" "}
          <span className="tabular-nums">+{formatNumber(item.bonus)}</span>
        </p>
      </div>

      {/* 총 지급 스타디아 */}
      <div className="flex-1 flex items-center justify-center px-5 pt-0 pb-4 sm:pb-6">
        <span className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#CF3688] tabular-nums">
          {formatNumber(item.totalStadia)}
        </span>
      </div>

      {/* 지불 비용 */}
      <div className="px-4 pb-4 pt-0">
        <div className="bg-[#ffd700] rounded-xl py-3 px-4 text-right">
          <span className="text-lg sm:text-xl font-bold text-black tabular-nums">
            {item.cost}
          </span>
        </div>
      </div>
    </div>
  );
};

const PricingComponent = () => {
  const priceData: PriceItem[] = [
    { stadia: 1000, bonus: 0, totalStadia: 1000, cost: "$1.09" },
    { stadia: 5000, bonus: 0, totalStadia: 5000, cost: "$5.29" },
    { stadia: 10000, bonus: 0, totalStadia: 10000, cost: "$10.49" },
    { stadia: 30000, bonus: 0, totalStadia: 30000, cost: "$31.49" },
    { stadia: 50000, bonus: 2000, totalStadia: 52000, cost: "$52.49", badgeKey: "recommended" },
    { stadia: 100000, bonus: 5000, totalStadia: 105000, cost: "$104.99", badgeKey: "bestSeller" },
    { stadia: 200000, bonus: 16000, totalStadia: 216000, cost: "$209.99" },
  ];

  return (
    <main className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
        {/* 헤더 */}
        <header className="text-center mb-8 sm:mb-12">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
            {getLanguageValue(PAGE_TYPE.COMMON, "price.txtTitle")}
          </h1>
          <p className="text-sm sm:text-base text-gray-500 max-w-md mx-auto">
            {getLanguageValue(PAGE_TYPE.COMMON, "price.txtDesc")}
          </p>
        </header>

        {/* 카드 그리드 - 모바일·태블릿·PC 공통 */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5 lg:gap-6">
          {priceData.map((item, index) => (
            <PriceCard key={index} item={item} />
          ))}
        </div>
      </div>
    </main>
  );
};

export default PricingComponent;
